import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Container,
    Paper,
    AppBar,
    Toolbar,
    IconButton,
    TextField,
    InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import {
    getLogs,
    getLogSummary,
    downloadLogs,
    downloadAllLogs,
    clearAllLogs,
    clearLogsForDate
} from '../utils/logger';

// Static password for accessing logs
const ACCESS_PASSWORD = 'Accr@123';

const ErrorLogs = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [logs, setLogs] = useState({});
    const [summary, setSummary] = useState({});
    const [selectedDate, setSelectedDate] = useState('');
    const [expandedLog, setExpandedLog] = useState(null);
    const navigate = useNavigate();

    // Calculate min and max dates (past 7 days only)
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const minDate = sevenDaysAgo.toISOString().split('T')[0];

    useEffect(() => {
        if (isAuthenticated) {
            refreshLogs();
        }
    }, [isAuthenticated]);

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        if (password === ACCESS_PASSWORD) {
            setIsAuthenticated(true);
            setPasswordError('');
        } else {
            setPasswordError('Incorrect password. Please try again.');
            setPassword('');
        }
    };

    const refreshLogs = () => {
        const allLogs = getLogs();
        const logSummary = getLogSummary();
        setLogs(allLogs);
        setSummary(logSummary);

        // Auto-select today's date
        const today = new Date().toISOString().split('T')[0];
        setSelectedDate(today);
    };

    const getFilteredLogs = () => {
        let entries = [];

        if (!selectedDate) {
            // Show all logs if no date selected
            Object.keys(logs).forEach(date => {
                logs[date].forEach(entry => {
                    entries.push({ ...entry, date });
                });
            });
        } else {
            // Show logs for selected date
            entries = (logs[selectedDate] || []).map(entry => ({ ...entry, date: selectedDate }));
        }

        // Sort entries in descending order (newest first) by timestamp
        entries.sort((a, b) => {
            // Compare by date first
            if (a.date !== b.date) {
                return b.date.localeCompare(a.date);
            }
            // If same date, compare by timestamp (assuming format like "28/11/2025, 10:30:45 AM")
            return b.timestamp.localeCompare(a.timestamp);
        });

        return entries;
    };

    const handleDownload = () => {
        if (!selectedDate) {
            downloadAllLogs();
        } else {
            downloadLogs(selectedDate);
        }
    };

    const handleClear = () => {
        if (window.confirm('Are you sure you want to delete these logs?')) {
            if (!selectedDate) {
                clearAllLogs();
            } else {
                clearLogsForDate(selectedDate);
            }
            refreshLogs();
        }
    };

    const handleShare = async () => {
        const filteredLogs = getFilteredLogs();
        if (filteredLogs.length === 0) {
            alert('No logs to share');
            return;
        }

        let logText = `BOPIS BillBerry Error Logs\n`;
        logText += `Date: ${!selectedDate ? 'All Dates' : selectedDate}\n`;
        logText += `Total Entries: ${filteredLogs.length}\n\n`;

        filteredLogs.forEach((entry, index) => {
            logText += `--- Entry #${index + 1} ---\n`;
            logText += `Date: ${entry.date}\n`;
            logText += `Time: ${entry.timestamp}\n`;
            logText += `Level: ${entry.level}\n`;
            logText += `Source: ${entry.source}\n`;
            logText += `Message: ${entry.message}\n`;
            logText += `Details: ${JSON.stringify(entry.details, null, 2)}\n\n`;
        });

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'BOPIS Error Logs',
                    text: logText,
                });
            } catch (err) {
                copyToClipboard(logText);
            }
        } else {
            copyToClipboard(logText);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Logs copied to clipboard!');
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Logs copied to clipboard!');
        }
    };

    const getLevelIcon = (level) => {
        switch (level) {
            case 'ERROR':
                return <ErrorIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
            case 'WARN':
                return <WarningIcon sx={{ color: '#ed6c02', fontSize: 20 }} />;
            case 'INFO':
                return <InfoIcon sx={{ color: '#0288d1', fontSize: 20 }} />;
            case 'DEBUG':
                return <BugReportIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
            default:
                return <ErrorIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
        }
    };

    const getLevelColor = (level) => {
        switch (level) {
            case 'ERROR': return 'error';
            case 'WARN': return 'warning';
            case 'INFO': return 'info';
            case 'DEBUG': return 'default';
            default: return 'error';
        }
    };

    // Password Screen
    if (!isAuthenticated) {
        return (
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}>
                <Paper sx={{
                    p: 4,
                    maxWidth: 400,
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: 2,
                    boxShadow: 3
                }}>
                    <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px'
                    }}>
                        <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>

                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                        Access Protected
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Enter password to view error logs
                    </Typography>

                    <form onSubmit={handlePasswordSubmit}>
                        <TextField
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            label="Password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordError('');
                            }}
                            error={!!passwordError}
                            helperText={passwordError}
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={!password}
                            sx={{ mb: 2 }}
                        >
                            Access Logs
                        </Button>

                        <Button
                            variant="text"
                            color="inherit"
                            onClick={() => navigate(-1)}
                            sx={{ color: '#666' }}
                        >
                            Go Back
                        </Button>
                    </form>
                </Paper>
            </Box>
        );
    }

    // Main Logs View (after authentication)
    const filteredLogs = getFilteredLogs();
    const totalLogs = Object.values(summary).reduce((a, b) => a + b, 0);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => navigate(-1)}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <BugReportIcon sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Error Logs
                    </Typography>
                    <Chip
                        label={`${totalLogs} total`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 2 }}>
                {/* Controls */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Date Selection Row */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                            <TextField
                                type="date"
                                size="small"
                                label="Select Date (Past 7 days)"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: minDate,
                                    max: today,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CalendarTodayIcon sx={{ fontSize: 18, color: '#666' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ minWidth: 220 }}
                            />
                            <Button
                                variant={!selectedDate ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setSelectedDate('')}
                            >
                                Show All ({totalLogs})
                            </Button>
                            {selectedDate && logs[selectedDate] && (
                                <Chip
                                    label={`${logs[selectedDate].length} logs for ${selectedDate}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {selectedDate && !logs[selectedDate] && (
                                <Chip
                                    label="No logs for this date"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        {/* Action Buttons Row */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={refreshLogs}
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                                disabled={filteredLogs.length === 0}
                            >
                                Download
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleShare}
                                disabled={filteredLogs.length === 0}
                            >
                                Share/Copy
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClear}
                                disabled={filteredLogs.length === 0}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Log Entries */}
                {filteredLogs.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <BugReportIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                            No error logs found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Logs will appear here when errors occur
                        </Typography>
                    </Paper>
                ) : (
                    filteredLogs.map((entry, index) => (
                        <Accordion
                            key={`${entry.date}-${index}`}
                            expanded={expandedLog === `${entry.date}-${index}`}
                            onChange={() => setExpandedLog(
                                expandedLog === `${entry.date}-${index}` ? null : `${entry.date}-${index}`
                            )}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', pr: 2 }}>
                                    {getLevelIcon(entry.level)}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {entry.source}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {entry.message}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {entry.date} | {entry.timestamp}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                                <Box sx={{ fontSize: '12px' }}>
                                    <Box sx={{ mb: 1 }}>
                                        <Chip
                                            label={entry.level}
                                            size="small"
                                            color={getLevelColor(entry.level)}
                                            sx={{ mr: 1 }}
                                        />
                                        <Chip
                                            label={entry.date}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
                                        Source:
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {entry.source}
                                    </Typography>

                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
                                        Message:
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {entry.message}
                                    </Typography>

                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
                                        Details:
                                    </Typography>
                                    <Box
                                        component="pre"
                                        sx={{
                                            bgcolor: '#263238',
                                            color: '#aed581',
                                            p: 1.5,
                                            borderRadius: 1,
                                            overflow: 'auto',
                                            fontSize: '11px',
                                            maxHeight: '400px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {JSON.stringify(entry.details, null, 2)}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Container>
        </Box>
    );
};

export default ErrorLogs;
